import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import ContextualHelp from '@/components/ContextualHelp';
import AffirmationCard from '@/components/AffirmationCard';
import {
  ClipboardList, ArrowRight, Flame, Sparkles,
  CheckCircle2, Lock, Info,
} from 'lucide-react';

const HOME_HELP = {
  whatIs: "Home is your personalized dashboard \u2014 a compact answer to one question: what should I do right now?",
  whyMatters: "Everything on Home adapts to your recent check-ins, streaks, and progress. It's designed to save you from decision fatigue and get you into the one action that matters most today.",
  howToUse: "Open Home first each day. Do the check-in if it's pending. Then come back tomorrow.",
  tips: [
    "Consistency matters more than intensity \u2014 a 60-second check-in daily beats a 20-minute weekly one.",
    "Everything else lives on its own page in the navigation \u2014 Home stays intentionally quiet.",
  ],
};

function greetingKey(hour) {
  if (hour < 5) return 'greeting_night';
  if (hour < 12) return 'greeting_morning';
  if (hour < 17) return 'greeting_afternoon';
  if (hour < 21) return 'greeting_evening';
  return 'greeting_late_night';
}

export default function Home() {
  const { t } = useTranslation();
  const [brief, setBrief] = useState(null);
  const [weeklyHack, setWeeklyHack] = useState(null);
  const [scoreInfo, setScoreInfo] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    http.get('/home/brief').then(r => setBrief(r.data)).catch(() => setBrief({}));
    http.get('/life-hacks/weekly').then(r => setWeeklyHack(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!brief) {
    return (
      <div className="px-5 lg:px-10 pt-6 space-y-5" data-testid="home-loading">
        <div className="h-8 w-48 rounded-lg bg-navy/5 animate-pulse" />
        <div className="h-44 rounded-[24px] bg-navy/5 animate-pulse" />
        <div className="h-24 rounded-[24px] bg-navy/5 animate-pulse" />
      </div>
    );
  }

  const firstName = (brief?.name || brief?.user_name || 'brother').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = t(`home.${greetingKey(hour)}`);
  const encIdx = ((new Date().getDate() + firstName.length) % 6) + 1;
  const enc = t(`home.enc_${encIdx}`);

  const wellness = brief?.wellness_score;
  const streak = brief?.streak || 0;
  const hasCheckIn = brief?.has_checkin_today;
  const checkinStatus = brief?.checkin_status || { can_checkin: !hasCheckIn };
  const canCheckin = !!checkinStatus?.can_checkin;
  const wellnessExplainer = brief?.wellness_boost_explainer;

  const nextStep = brief?.next_step;

  const onCheckinClick = () => {
    if (canCheckin) { navigate('/app/checkin'); return; }
    toast.info(
      checkinStatus?.message || "You've checked in today. Come back tomorrow for your next check-in.",
      { duration: 4200 },
    );
  };

  return (
    <div className="px-5 lg:px-10 pt-5 lg:pt-8 pb-10 space-y-7 lg:space-y-9" data-testid="home-page">

      {/* 1. PERSONAL GREETING — editorial, confident, compact on mobile */}
      <section className="animate-fade-up pt-1" data-testid="home-greeting">
        <p className="text-[11px] uppercase tracking-[0.22em] text-bronze font-semibold">{t('home.eyebrow')}</p>
        <h1 className="font-heading text-[28px] leading-[1.12] sm:text-4xl lg:text-5xl mt-1.5 text-navy flex items-start gap-2 flex-wrap">
          <span>{greeting}, {firstName}.</span>
          <ContextualHelp pageTitle="Home" content={HOME_HELP} testid="home-help"/>
        </h1>
        <p className="text-navy/55 text-[16px] sm:text-lg mt-2 leading-relaxed max-w-2xl">{enc}</p>
      </section>

      {/* 2. PRIMARY DAILY CHECK-IN — the strongest element on the page */}
      <section data-testid="home-checkin-hero">
        <div
          className="relative overflow-hidden rounded-[24px] bg-navy text-white"
          style={{ boxShadow: '0 18px 44px -18px rgba(11,23,38,0.55)' }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(145deg, #0B1726 0%, #15263B 55%, #20354A 100%)' }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(520px 200px at 90% -10%, rgba(198,165,104,0.20), transparent 60%)' }} />
          <div className="relative p-6 lg:p-9">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full flex items-center justify-center bg-white/10 border border-white/10">
                <ClipboardList size={15} className="text-gold" strokeWidth={2}/>
              </span>
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">
                {hasCheckIn ? 'Today\u2019s check-in' : 'Daily check-in'}
              </p>
            </div>

            {canCheckin ? (
              <>
                <h2 className="font-heading text-[22px] lg:text-[28px] mt-4 leading-tight">How are you feeling today?</h2>
                <p className="text-white/65 text-[15px] lg:text-base mt-2 max-w-md leading-relaxed">Take a moment. Check in with yourself.</p>
                <button
                  type="button"
                  data-testid="home-checkin-start"
                  onClick={onCheckinClick}
                  className="group mt-6 inline-flex items-center gap-2 rounded-full bg-gold text-navy font-semibold text-sm px-6 py-3.5 min-h-[44px] hover:brightness-105 active:scale-[0.98] transition-all"
                >
                  Start my check-in
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5"/>
                </button>
                <p className="text-white/40 text-xs mt-3">Under 60 seconds.</p>
              </>
            ) : (
              <>
                <h2 className="font-heading text-[22px] lg:text-[28px] mt-4 leading-tight inline-flex items-center gap-2.5">
                  <CheckCircle2 size={24} className="text-sage shrink-0" strokeWidth={2}/> You’ve checked in today.
                </h2>
                <p className="text-white/65 text-[15px] lg:text-base mt-2 max-w-md leading-relaxed">
                  {checkinStatus?.message || 'Nice work showing up. Your next check-in unlocks tomorrow.'}
                </p>
                {nextStep && nextStep.kind !== 'checkin' && (
                  <Link
                    to={nextStep.cta_to}
                    data-testid="home-checkin-nextstep"
                    className="group mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 text-white font-semibold text-sm px-6 py-3.5 min-h-[44px] hover:bg-white/15 transition-all"
                  >
                    {nextStep.cta_label || 'Continue'}
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5"/>
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* 3. COMPACT PROGRESS SUMMARY — real data, info control instead of a paragraph */}
      <section
        className="rounded-[24px] bg-white border border-navy/[0.06] px-5 py-4 lg:px-7 lg:py-5"
        style={{ boxShadow: '0 6px 24px rgba(11,23,38,0.05)' }}
        data-testid="home-snapshot"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-navy/45 font-semibold">Your progress</p>
          <button
            type="button"
            data-testid="home-score-info"
            onClick={() => setScoreInfo(v => !v)}
            aria-expanded={scoreInfo}
            aria-label="How the wellness score works"
            className="inline-flex items-center gap-1 text-[11px] text-navy/50 hover:text-navy transition-smooth rounded-full px-2 py-1 min-h-[32px]"
          >
            <Info size={13}/> How it works
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 lg:gap-6 divide-x divide-navy/[0.06]">
          <SnapshotCell
            label={t('home.wellness_score')}
            value={wellness ?? '\u2014'}
            sub={wellness != null ? '/ 100' : t('home.take_assessment')}
          />
          <SnapshotCell
            label={t('home.current_streak')}
            value={streak}
            sub={streak === 1 ? t('home.day') : t('home.days')}
            icon={streak >= 3 ? Flame : null}
            tone="sage"
          />
          <button
            type="button"
            onClick={onCheckinClick}
            data-testid="home-checkin-cell"
            className="px-2 first:pl-0 text-left transition-smooth hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-gold/60 rounded-lg min-h-[44px]"
          >
            <p className="text-[10px] uppercase tracking-[0.16em] text-navy/45 font-semibold">{t('home.todays_checkin')}</p>
            <div className="flex items-baseline gap-2 mt-1.5">
              <p className="font-heading text-xl lg:text-3xl font-light tabular-nums leading-none" style={{ color: hasCheckIn ? '#657F73' : '#0B1726' }}>
                {hasCheckIn ? t('home.checkin_done') : t('home.checkin_pending')}
              </p>
              {hasCheckIn
                ? <CheckCircle2 size={15} className="text-sage" strokeWidth={1.75}/>
                : <Lock size={12} className="text-navy/30" strokeWidth={1.75}/>}
            </div>
            <p className="text-[11px] text-navy/45 mt-1">{hasCheckIn ? 'unlocks tomorrow' : '60 seconds'}</p>
          </button>
        </div>
        {scoreInfo && wellnessExplainer && (
          <p data-testid="home-score-explainer" className="text-[13px] text-navy/60 leading-relaxed mt-4 pt-4 border-t border-navy/[0.06]">
            {wellnessExplainer}
          </p>
        )}
      </section>

      {/* 4. HACK OF THE WEEK — slim editorial strip */}
      {weeklyHack?.hack && (
        <section data-testid="home-weekly-hack">
          <Link
            to={`/app/learn/lesson/${weeklyHack.lesson_id}`}
            data-testid="home-weekly-hack-link"
            className="group flex items-start gap-3 rounded-[22px] p-4 lg:p-5 bg-beige border border-navy/[0.05] hover:-translate-y-0.5 transition-all"
          >
            <span className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles size={15} className="text-bronze" strokeWidth={2}/>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-bronze font-semibold">Hack of the week</p>
              <p className="font-heading text-[16px] lg:text-lg text-navy leading-snug mt-1">{weeklyHack.hack}</p>
              <p className="text-[12px] text-navy/50 mt-1.5 inline-flex items-center gap-1">From {weeklyHack.title} <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5"/></p>
            </div>
          </Link>
        </section>
      )}

      {/* 5. TODAY'S AFFIRMATION */}
      <AffirmationCard />

      {/* Care disclaimer — single, compact (legal/crisis/copyright live in the app footer) */}
      <footer className="max-w-2xl mx-auto pt-5 border-t border-navy/[0.06]" data-testid="home-therapy-disclaimer">
        <p className="text-[11px] text-navy/45 text-center leading-relaxed">
          Brotherhood+ supports your wellness and growth. It is{' '}
          <span className="font-semibold text-navy/60">not a substitute for a licensed therapist</span>. In crisis, call or text{' '}
          <a href="tel:988" className="font-semibold text-navy/70 underline underline-offset-2">988</a>.
        </p>
      </footer>
    </div>
  );
}

function SnapshotCell({ label, value, sub, tone, icon: Icon }) {
  const valueColor = tone === 'sage' ? '#657F73' : '#0B1726';
  return (
    <div className="px-2 first:pl-0">
      <p className="text-[10px] uppercase tracking-[0.16em] text-navy/45 font-semibold">{label}</p>
      <div className="flex items-baseline gap-2 mt-1.5">
        <p className="font-heading text-xl lg:text-3xl font-light tabular-nums leading-none" style={{ color: valueColor }}>{value}</p>
        {Icon && <Icon size={15} className={tone === 'sage' ? 'text-sage' : 'text-bronze'} strokeWidth={1.75}/>}
      </div>
      <p className="text-[11px] text-navy/45 mt-1">{sub}</p>
    </div>
  );
}
